import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SITE_REGISTRY from '../SiteRegistry';

const API = process.env.REACT_APP_API_URL || '';

// ── Helpers ──────────────────────────────────────────────────────────────────
function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

// ── Field components ─────────────────────────────────────────────────────────
function Field({ label, value, onChange, type = 'text', rows, placeholder }) {
  const base = {
    width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, color: '#EAEAEA', fontSize: '0.82rem', padding: '8px 10px',
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: '0.68rem', color: '#666', marginBottom: 4, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
      {rows
        ? <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} style={{ ...base, resize: 'vertical', lineHeight: 1.5 }}/>
        : type === 'color'
          ? <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} style={{ width: 36, height: 30, border: 'none', borderRadius: 6, cursor: 'pointer', padding: 2, background: 'transparent' }}/>
              <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...base, flex: 1 }}/>
            </div>
          : <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={base}/>
      }
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C8FF00', marginTop: 20, marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      {children}
    </div>
  );
}

function ArrayEditor({ label, items = [], onChange, fields, addDefault }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: '0.68rem', color: '#666', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
        <button onClick={() => onChange([...items, { ...addDefault }])}
          style={{ background: 'rgba(200,255,0,0.12)', border: '1px solid rgba(200,255,0,0.2)', borderRadius: 6, color: '#C8FF00', fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', cursor: 'pointer' }}>
          + Ajouter
        </button>
      </div>
      {items.map((item, i) => (
        <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '10px 12px', marginBottom: 8, position: 'relative' }}>
          <button onClick={() => onChange(items.filter((_, j) => j !== i))}
            style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', color: '#444', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700 }}>×</button>
          {fields.map(f => (
            <Field key={f.key} label={f.label} value={item[f.key]} rows={f.rows} type={f.type}
              onChange={v => { const arr = deepClone(items); arr[i][f.key] = v; onChange(arr); }}/>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Tab panels ────────────────────────────────────────────────────────────────
function TabGeneral({ cfg, set }) {
  return (
    <>
      <SectionTitle>Identité</SectionTitle>
      <Field label="Nom du site" value={cfg.name} onChange={v => set('name', v)}/>
      <Field label="Tagline" value={cfg.tagline} rows={2} onChange={v => set('tagline', v)}/>
      <Field label="Ville / Localisation" value={cfg.location} onChange={v => set('location', v)}/>
      <Field label="Logo URL" value={cfg.logo} placeholder="https://..." onChange={v => set('logo', v)}/>
      <Field label="Label bouton principal" value={cfg.ctaLabel} onChange={v => set('ctaLabel', v)}/>

      <SectionTitle>Contact</SectionTitle>
      <Field label="Téléphone" value={cfg.phone} onChange={v => set('phone', v)}/>
      <Field label="Email" value={cfg.email} onChange={v => set('email', v)}/>
      <Field label="Adresse" value={cfg.address} onChange={v => set('address', v)}/>

      <SectionTitle>Réseaux sociaux</SectionTitle>
      <Field label="Facebook" value={cfg.social?.facebook} placeholder="https://facebook.com/..." onChange={v => set('social', { ...cfg.social, facebook: v })}/>
      <Field label="Instagram" value={cfg.social?.instagram} placeholder="https://instagram.com/..." onChange={v => set('social', { ...cfg.social, instagram: v })}/>
      <Field label="LinkedIn" value={cfg.social?.linkedin} placeholder="https://linkedin.com/..." onChange={v => set('social', { ...cfg.social, linkedin: v })}/>
    </>
  );
}

function TabHero({ cfg, set }) {
  const setHero = (k, v) => set('hero', { ...cfg.hero, [k]: v });
  return (
    <>
      <SectionTitle>Images</SectionTitle>
      <Field label="Image hero (URL)" value={cfg.heroImage} placeholder="https://images.unsplash.com/..." onChange={v => set('heroImage', v)}/>
      <Field label="Photo À propos (URL)" value={cfg.aboutPhoto} placeholder="https://images.unsplash.com/..." onChange={v => set('aboutPhoto', v)}/>

      <SectionTitle>Textes Hero</SectionTitle>
      <Field label="Eyebrow (petite ligne)" value={cfg.hero?.eyebrow} onChange={v => setHero('eyebrow', v)}/>
      <Field label="Titre ligne 1" value={cfg.hero?.titleLine1} onChange={v => setHero('titleLine1', v)}/>
      <Field label="Titre accent 1 (gradient)" value={cfg.hero?.titleAccent} onChange={v => setHero('titleAccent', v)}/>
      <Field label="Titre ligne 2" value={cfg.hero?.titleLine2} onChange={v => setHero('titleLine2', v)}/>
      <Field label="Titre accent 2 (gradient)" value={cfg.hero?.titleAccent2} onChange={v => setHero('titleAccent2', v)}/>
      <Field label="Sous-titre" value={cfg.hero?.subtitle} rows={3} onChange={v => setHero('subtitle', v)}/>
      <Field label="CTA principal" value={cfg.hero?.cta} onChange={v => setHero('cta', v)}/>
      <Field label="CTA secondaire" value={cfg.hero?.ctaSecond} onChange={v => setHero('ctaSecond', v)}/>

      <SectionTitle>Stats</SectionTitle>
      <ArrayEditor label="Statistiques" items={cfg.stats || []} onChange={v => set('stats', v)}
        addDefault={{ value: '', label: '' }}
        fields={[{ key: 'value', label: 'Valeur (ex: 10+)' }, { key: 'label', label: 'Label' }]}/>
    </>
  );
}

function TabServices({ cfg, set }) {
  return (
    <>
      <SectionTitle>Services</SectionTitle>
      <ArrayEditor label="Services" items={cfg.services || []} onChange={v => set('services', v)}
        addDefault={{ icon: '◈', title: '', desc: '', image: '' }}
        fields={[
          { key: 'icon', label: 'Icône (symbole)' },
          { key: 'title', label: 'Titre' },
          { key: 'desc', label: 'Description', rows: 2 },
          { key: 'image', label: 'Image URL' },
        ]}/>
    </>
  );
}

function TabRealisations({ cfg, set }) {
  return (
    <>
      <SectionTitle>Réalisations</SectionTitle>
      <ArrayEditor label="Projets portfolio" items={cfg.realisations || []} onChange={v => set('realisations', v)}
        addDefault={{ name: '', category: '', url: '', image: '' }}
        fields={[
          { key: 'name', label: 'Nom du projet' },
          { key: 'category', label: 'Catégorie' },
          { key: 'url', label: 'URL du site' },
          { key: 'image', label: 'Image URL' },
        ]}/>
    </>
  );
}

function TabProcessus({ cfg, set }) {
  return (
    <>
      <SectionTitle>Étapes du processus</SectionTitle>
      <ArrayEditor label="Processus" items={cfg.process || []} onChange={v => set('process', v)}
        addDefault={{ step: '01', title: '', desc: '' }}
        fields={[
          { key: 'step', label: 'Numéro (01, 02...)' },
          { key: 'title', label: 'Titre' },
          { key: 'desc', label: 'Description', rows: 2 },
        ]}/>

      <SectionTitle>À propos</SectionTitle>
      <Field label="Nom (À propos)" value={cfg.about?.title} onChange={v => set('about', { ...cfg.about, title: v })}/>
      <Field label="Bio" value={cfg.about?.text} rows={4} onChange={v => set('about', { ...cfg.about, text: v })}/>
    </>
  );
}

function TabTemoignages({ cfg, set }) {
  return (
    <>
      <SectionTitle>Témoignages</SectionTitle>
      <ArrayEditor label="Témoignages clients" items={cfg.testimonials || []} onChange={v => set('testimonials', v)}
        addDefault={{ name: '', role: '', stars: 5, text: '' }}
        fields={[
          { key: 'name', label: 'Nom' },
          { key: 'role', label: 'Rôle / Titre' },
          { key: 'stars', label: 'Étoiles (1-5)' },
          { key: 'text', label: 'Témoignage', rows: 3 },
        ]}/>
    </>
  );
}

function TabCouleurs({ cfg, set }) {
  return (
    <>
      <SectionTitle>Palette</SectionTitle>
      <Field label="Accent from (début gradient)" value={cfg.accentFrom} type="color" onChange={v => set('accentFrom', v)}/>
      <Field label="Accent to (fin gradient)" value={cfg.accentTo} type="color" onChange={v => set('accentTo', v)}/>
      <Field label="Fond sombre (darkBg)" value={cfg.darkBg} type="color" onChange={v => set('darkBg', v)}/>

      <SectionTitle>Navigation</SectionTitle>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: '0.68rem', color: '#666', marginBottom: 6, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Liens de navigation</div>
        {(cfg.nav || []).map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <input value={item} onChange={e => {
              const nav = [...(cfg.nav || [])]; nav[i] = e.target.value; set('nav', nav);
            }} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#EAEAEA', fontSize: '0.82rem', padding: '7px 10px', outline: 'none', fontFamily: 'inherit' }}/>
            <button onClick={() => set('nav', (cfg.nav || []).filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', color: '#444', cursor: 'pointer', fontSize: '0.9rem', padding: '4px 8px' }}>×</button>
          </div>
        ))}
        <button onClick={() => set('nav', [...(cfg.nav || []), 'Nouveau'])}
          style={{ background: 'rgba(200,255,0,0.1)', border: '1px solid rgba(200,255,0,0.2)', borderRadius: 6, color: '#C8FF00', fontSize: '0.7rem', fontWeight: 700, padding: '4px 12px', cursor: 'pointer', marginTop: 4 }}>
          + Ajouter lien
        </button>
      </div>
    </>
  );
}

// ── Tabs config ───────────────────────────────────────────────────────────────
const TABS = [
  { id: 'general',      label: 'Général',      Icon: '⚙' },
  { id: 'hero',         label: 'Hero',          Icon: '◎' },
  { id: 'services',     label: 'Services',      Icon: '◈' },
  { id: 'realisations', label: 'Portfolio',     Icon: '◉' },
  { id: 'processus',    label: 'Processus',     Icon: '◆' },
  { id: 'temoignages',  label: 'Avis',          Icon: '★' },
  { id: 'couleurs',     label: 'Couleurs',      Icon: '●' },
];

// ── Main editor ───────────────────────────────────────────────────────────────
export default function SiteEditor() {
  const { identifier } = useParams();
  const navigate = useNavigate();

  const [cfg,        setCfg]        = useState(null);
  const [savedCfg,   setSavedCfg]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [error,      setError]      = useState('');
  const [activeTab,  setActiveTab]  = useState('general');
  const [previewMode,setPreviewMode]= useState('desktop');

  const SiteApp = SITE_REGISTRY[identifier];

  // Charger la config depuis le serveur
  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/site-editor/${identifier}/config`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setLoading(false); return; }
        setCfg(deepClone(data));
        setSavedCfg(deepClone(data));
        setLoading(false);
      })
      .catch(() => { setError('Impossible de charger la config.'); setLoading(false); });
  }, [identifier]);

  const set = useCallback((key, value) => {
    setCfg(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/site-editor/${identifier}/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cfg),
      });
      const data = await res.json();
      if (data.success) {
        setSavedCfg(deepClone(cfg));
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(data.error || 'Erreur de sauvegarde');
      }
    } catch { setError('Erreur réseau'); }
    setSaving(false);
  };

  const isDirty = cfg && savedCfg && JSON.stringify(cfg) !== JSON.stringify(savedCfg);

  // ── Render ─────────────────────────────────────────────────────────────────
  const s = {
    root: { display: 'flex', flexDirection: 'column', height: '100vh', background: '#0a0a0a', color: '#EAEAEA', fontFamily: '"Inter",-apple-system,sans-serif', overflow: 'hidden' },
    topbar: { height: 52, background: '#111', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12, flexShrink: 0, zIndex: 10 },
    body: { display: 'flex', flex: 1, overflow: 'hidden' },
    panel: { width: 340, background: '#0e0e0e', borderRight: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' },
    tabs: { display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto', flexShrink: 0 },
    fields: { flex: 1, overflowY: 'auto', padding: '12px 16px 40px' },
    preview: { flex: 1, background: '#050505', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' },
  };

  if (loading) return (
    <div style={{ ...s.root, alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <div style={{ width: 32, height: 32, border: '3px solid rgba(200,255,0,0.3)', borderTopColor: '#C8FF00', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: '#555', fontSize: '0.88rem' }}>Chargement de {identifier}…</div>
    </div>
  );

  if (error && !cfg) return (
    <div style={{ ...s.root, alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <div style={{ color: '#ff6b6b', fontSize: '0.95rem' }}>{error}</div>
      <button onClick={() => navigate('/dashboard')} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#EAEAEA', padding: '8px 16px', cursor: 'pointer', fontSize: '0.85rem' }}>← Dashboard</button>
    </div>
  );

  const SCALE = previewMode === 'mobile' ? 0.42 : 0.52;
  const PREVIEW_W = previewMode === 'mobile' ? 390 : 1280;

  return (
    <div style={s.root}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:99px}
        input:focus,textarea:focus{border-color:rgba(200,255,0,0.4)!important;box-shadow:0 0 0 2px rgba(200,255,0,0.08)}
      `}</style>

      {/* ── Topbar ── */}
      <div style={s.topbar}>
        <button onClick={() => navigate('/dashboard')}
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#888', padding: '6px 12px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Dashboard
        </button>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#C8FF00', boxShadow: '0 0 6px #C8FF00' }}/>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: '-0.02em' }}>{cfg?.name || identifier}</span>
          <span style={{ fontSize: '0.72rem', color: '#444', padding: '2px 8px', background: 'rgba(255,255,255,0.04)', borderRadius: 99, border: '1px solid rgba(255,255,255,0.07)' }}>{identifier}</span>
          {isDirty && <span style={{ fontSize: '0.68rem', color: '#FF6B2B', fontWeight: 700 }}>● Non sauvegardé</span>}
        </div>

        {/* Mode desktop / mobile */}
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, overflow: 'hidden' }}>
          {[['desktop','🖥'], ['mobile','📱']].map(([m, icon]) => (
            <button key={m} onClick={() => setPreviewMode(m)}
              style={{ background: previewMode === m ? 'rgba(200,255,0,0.15)' : 'none', border: 'none', color: previewMode === m ? '#C8FF00' : '#444', padding: '6px 14px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.15s' }}>
              {icon}
            </button>
          ))}
        </div>

        <button onClick={handleSave} disabled={saving || !isDirty}
          style={{ background: saved ? 'rgba(100,220,100,0.15)' : isDirty ? 'linear-gradient(135deg,#C8FF00,#94CC00)' : 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 8, color: saved ? '#6DDD6D' : isDirty ? '#000' : '#333', padding: '8px 20px', cursor: isDirty ? 'pointer' : 'default', fontWeight: 700, fontSize: '0.82rem', transition: 'all 0.2s', minWidth: 110 }}>
          {saving ? '...' : saved ? '✓ Sauvegardé' : 'Sauvegarder'}
        </button>
      </div>

      {/* ── Body ── */}
      <div style={s.body}>

        {/* ── Panneau gauche ── */}
        <div style={s.panel}>
          {/* Tabs */}
          <div style={s.tabs}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                style={{ background: 'none', border: 'none', borderBottom: activeTab === t.id ? '2px solid #C8FF00' : '2px solid transparent', color: activeTab === t.id ? '#C8FF00' : '#555', padding: '10px 11px 9px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap', transition: 'color 0.15s', textTransform: 'uppercase' }}>
                <span style={{ marginRight: 4 }}>{t.Icon}</span>{t.label}
              </button>
            ))}
          </div>

          {/* Champs */}
          <div style={s.fields}>
            {cfg && activeTab === 'general'      && <TabGeneral      cfg={cfg} set={set}/>}
            {cfg && activeTab === 'hero'         && <TabHero         cfg={cfg} set={set}/>}
            {cfg && activeTab === 'services'     && <TabServices     cfg={cfg} set={set}/>}
            {cfg && activeTab === 'realisations' && <TabRealisations cfg={cfg} set={set}/>}
            {cfg && activeTab === 'processus'    && <TabProcessus    cfg={cfg} set={set}/>}
            {cfg && activeTab === 'temoignages'  && <TabTemoignages  cfg={cfg} set={set}/>}
            {cfg && activeTab === 'couleurs'     && <TabCouleurs     cfg={cfg} set={set}/>}
          </div>
        </div>

        {/* ── Aperçu ── */}
        <div style={s.preview}>
          {/* Label */}
          <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 99, padding: '4px 14px', fontSize: '0.68rem', color: '#555', fontWeight: 600, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
            Aperçu live — {previewMode === 'mobile' ? '390px mobile' : '1280px desktop'} @ {Math.round(SCALE * 100)}%
          </div>

          {!SiteApp ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: '2rem' }}>🔧</div>
              <div style={{ color: '#555', fontSize: '0.88rem', textAlign: 'center' }}>
                Aperçu non disponible pour ce site.<br/>
                <span style={{ color: '#333', fontSize: '0.78rem' }}>Sauvegardez et rechargez le site pour voir les changements.</span>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              <div style={{
                position: 'absolute', top: 0, left: previewMode === 'mobile' ? '50%' : 0,
                marginLeft: previewMode === 'mobile' ? `-${PREVIEW_W * SCALE / 2}px` : 0,
                width: PREVIEW_W, height: `${100 / SCALE}%`,
                transform: `scale(${SCALE})`, transformOrigin: 'top left',
                pointerEvents: 'none',
              }}>
                {cfg && <SiteApp overrideConfig={cfg}/>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
